import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Facebook, Twitter, MessageCircle, Copy, Crown, Clock } from "lucide-react";

import { api, API, formatApiError, getShareUrl } from "../lib/api";
import VoteForm from "../components/VoteForm";
import VoteConfirmation from "../components/VoteConfirmation";
import Countdown from "../components/Countdown";
import useVisibilityAwarePolling from "../hooks/useVisibilityAwarePolling";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

function asBool(v, def = true) {
  if (v === null || v === undefined) return def;
  if (v === "0" || v === "false" || v === "no") return false;
  if (v === "1" || v === "true" || v === "yes") return true;
  return def;
}

function asHex(v, def) {
  if (typeof v !== "string") return def;
  const s = v.startsWith("#") ? v : `#${v}`;
  return /^#[0-9A-Fa-f]{3,8}$/.test(s) ? s : def;
}

export default function EmbedWidget() {
  const [params] = useSearchParams();

  // Layout / sections
  const mode = ["vote", "results", "both"].includes(params.get("mode"))
    ? params.get("mode")
    : "both";
  const top = Math.max(0, Math.min(10, parseInt(params.get("top") || "3", 10)));
  const showHeader = asBool(params.get("header"));
  const showLogo = asBool(params.get("logo"));
  const showTitle = asBool(params.get("title"));
  const showDescription = asBool(params.get("description"), false);
  const showCountdown = asBool(params.get("countdown"));
  const showShare = asBool(params.get("share"), false);
  const showFooter = asBool(params.get("footer"));
  const compact = asBool(params.get("compact"), false);

  // Theme
  const theme = ["light", "dark", "transparent"].includes(params.get("theme"))
    ? params.get("theme")
    : "light";
  const accent = asHex(params.get("accent"), "#2e6c2e");
  const radius = Math.max(0, Math.min(40, parseInt(params.get("radius") || "20", 10)));
  const customBg = asHex(params.get("bg"), null);

  const [data, setData] = useState({ campaign: null, leaderboard: [], total_votes: 0 });
  const [loaded, setLoaded] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [votedId, setVotedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/campaign/active");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("allow-scroll");
    document.body.classList.add("allow-scroll");
    document.body.style.background = "transparent";
    fetchData();
    let es;
    try {
      es = new EventSource(`${API}/events`);
      es.addEventListener("vote", () => fetchData());
    } catch {
      /* SSE unsupported */
    }
    return () => {
      if (es) es.close();
    };
  }, [fetchData]);

  // Visibility-aware fallback polling for embed
  useVisibilityAwarePolling(fetchData, 30000);

  const campaign = data.campaign;
  const optionLabel = campaign?.option_label || "miejsce";
  const displayMode = campaign?.display_mode === "radio" ? "radio" : "combobox";
  const votingEnded =
    !!campaign?.ends_at && new Date(campaign.ends_at).getTime() <= Date.now();
  // When voting is finished, ignore vote-only mode and always force results
  const effectiveMode = votingEnded ? "results" : mode;
  const topList = useMemo(
    () => (data.leaderboard || []).slice(0, top),
    [data.leaderboard, top]
  );
  const max = topList[0]?.votes || 1;
  const winner = topList[0] || null;

  const onVoted = (option, voteCount) => {
    setConfirmation({ option, votes: voteCount });
    setVotedId(option.id);
    fetchData();
  };

  const handleShare = async (platform) => {
    const url = getShareUrl();
    const text = "Głosuj na Scootive On Tour!";
    let shareUrl = "";
    if (platform === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    if (platform === "tw") shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    if (platform === "wa") shareUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`;
    if (platform === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link skopiowany");
      } catch {
        toast.error("Nie udało się skopiować");
      }
      return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const isDark = theme === "dark";
  const cardBg =
    customBg || (theme === "transparent" ? "transparent" : isDark ? "#0f1410" : "#ffffff");
  const textCol = isDark ? "#f3f4f6" : "#0e1410";
  const subtleText = isDark ? "rgba(243,244,246,0.55)" : "rgba(14,20,16,0.45)";
  const borderCol = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  // In dark mode, the brand-green is too dark for text/icons — boost to a lighter tint of the chosen accent
  const accentText = isDark ? "#94d094" : accent;

  return (
    <div
      data-testid="embed-widget-root"
      className="min-h-screen w-full p-3 sm:p-4 flex items-start justify-center"
      style={{ background: "transparent", color: textCol }}
    >
      <div
        className={`w-full ${compact ? "max-w-sm" : "max-w-md"}`}
        style={{
          background: cardBg,
          color: textCol,
          borderRadius: `${radius}px`,
          boxShadow: theme === "transparent" ? "none" : "0 20px 50px -20px rgba(0,0,0,0.18)",
          border: theme === "transparent" ? "none" : `1px solid ${borderCol}`,
          padding: compact ? "16px" : "20px",
        }}
      >
        {showHeader && (
          <div className="flex items-center justify-between gap-3 mb-4">
            {showLogo && (
              <a
                href="https://www.scootive.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                style={{ filter: isDark ? "invert(1) hue-rotate(180deg)" : "none" }}
              >
                <img src={LOGO_URL} alt="Scootive" className="h-6 sm:h-7" />
              </a>
            )}
            {showFooter && (
              <span
                className="text-[9px] font-medium uppercase tracking-[0.3em]"
                style={{ color: subtleText }}
              >
                On Tour {new Date().getFullYear()}
              </span>
            )}
          </div>
        )}

        {showTitle && campaign && (
          <h2
            className="font-display uppercase tracking-tight leading-tight"
            style={{
              fontSize: compact ? "1.05rem" : "1.35rem",
              color: textCol,
            }}
          >
            {campaign.title}
          </h2>
        )}

        {showDescription && campaign?.description && (
          <p className="text-xs leading-relaxed mt-2" style={{ color: subtleText }}>
            {campaign.description}
          </p>
        )}

        {/* No active campaign state */}
        {loaded && !campaign && (
          <div
            data-testid="embed-no-campaign"
            className="py-8 text-center"
            style={{ color: subtleText }}
          >
            <Clock
              className="w-7 h-7 mx-auto mb-2.5 opacity-60"
              style={{ color: accentText }}
            />
            <p
              className="text-[10px] font-medium uppercase tracking-[0.3em] mb-1"
              style={{ color: accentText }}
            >
              Stay tuned
            </p>
            <p className="text-sm" style={{ color: textCol }}>
              Brak aktywnego głosowania
            </p>
            <p className="text-[11px] mt-1.5 leading-relaxed">
              Wróć wkrótce — kolejna kampania już niedługo.
            </p>
          </div>
        )}

        {/* Voting-ended badge */}
        {campaign && votingEnded && (
          <div
            data-testid="embed-voting-ended"
            className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: `${accent}1a`, color: accentText }}
          >
            <Clock className="w-3 h-3" />
            <span className="text-[9px] font-semibold uppercase tracking-[0.25em]">
              Głosowanie zakończone
            </span>
          </div>
        )}

        {/* Countdown — only if voting still open */}
        {showCountdown && campaign?.ends_at && !votingEnded && (
          <div className="mt-3 flex justify-start">
            <Countdown endsAt={campaign.ends_at} compact />
          </div>
        )}

        {/* Winner banner — when voting ended */}
        {campaign && votingEnded && winner && (
          <div
            data-testid="embed-winner-banner"
            className="mt-4 rounded-xl p-3 sm:p-3.5"
            style={{
              background: isDark
                ? `linear-gradient(135deg, ${accent}33 0%, ${accent}14 100%)`
                : `linear-gradient(135deg, ${accent}22 0%, ${accent}0a 100%)`,
              border: `1px solid ${accent}66`,
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Crown className="w-3.5 h-3.5" style={{ color: accentText }} />
              <p
                className="text-[9px] font-semibold uppercase tracking-[0.3em]"
                style={{ color: accentText }}
              >
                Zwycięża
              </p>
            </div>
            <div className="flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p
                  className="font-display uppercase leading-tight truncate"
                  style={{ fontSize: compact ? "1.1rem" : "1.35rem", color: textCol }}
                >
                  {winner.city}
                </p>
                {(winner.skatepark || winner.region) && (
                  <p
                    className="text-[10px] truncate mt-0.5"
                    style={{ color: subtleText }}
                  >
                    {winner.skatepark}
                    {winner.skatepark && winner.region && " · "}
                    {winner.region}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p
                  className="font-display tabular-nums leading-none"
                  style={{ fontSize: compact ? "1.15rem" : "1.5rem", color: accentText }}
                >
                  {winner.votes}
                </p>
                <p className="text-[9px] tabular-nums" style={{ color: subtleText }}>
                  {winner.percentage}%
                </p>
              </div>
            </div>
          </div>
        )}

        {/* VOTE block — hidden when voting ended */}
        {campaign && !votingEnded && (effectiveMode === "vote" || effectiveMode === "both") && (
          <>
            <p
              className="text-[10px] font-medium uppercase tracking-[0.25em] mt-4 mb-2"
              style={{ color: subtleText }}
            >
              Wybierz: <span style={{ color: accentText }}>{optionLabel}</span>
            </p>
            <VoteForm
              optionLabel={optionLabel}
              hasPostalCodes={!!campaign?.has_postal_codes}
              displayMode={displayMode}
              onVoted={onVoted}
              compact
            />
          </>
        )}

        {/* RESULTS block */}
        {campaign && (effectiveMode === "results" || effectiveMode === "both") && top > 0 && topList.length > 0 && (
          <div className={effectiveMode === "both" && !votingEnded ? "mt-5 pt-4 border-t" : "mt-4"} style={{ borderColor: borderCol }}>
            <div className="flex items-center justify-between mb-2">
              <p
                className="text-[10px] font-medium uppercase tracking-[0.25em]"
                style={{ color: accentText }}
              >
                Top {topList.length} {votingEnded ? "· Final" : "· Live"}
              </p>
              <p
                className="text-[10px] tabular-nums"
                style={{ color: subtleText }}
              >
                {data.total_votes} głosów
              </p>
            </div>
            <div className="space-y-1.5">
              {topList.map((row, idx) => {
                const pct = Math.max(4, (row.votes / max) * 100);
                const isVoted = row.skatepark_id === votedId;
                const isTopRow = idx === 0;
                return (
                  <div
                    key={row.skatepark_id}
                    className="relative rounded-lg overflow-hidden"
                    style={{
                      border: isVoted ? `2px solid ${accent}` : `1px solid ${borderCol}`,
                      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                    }}
                  >
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{
                        width: `${pct}%`,
                        background: isDark
                          ? `linear-gradient(90deg, ${accent}80 0%, ${accent}40 100%)`
                          : `linear-gradient(90deg, ${accent}30 0%, ${accent}12 100%)`,
                      }}
                    />
                    <div className="relative flex items-center justify-between gap-2 px-2.5 py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="font-display text-base leading-none w-5 shrink-0 tabular-nums"
                          style={{ color: isTopRow ? accentText : subtleText }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        {isTopRow && (
                          <Trophy className="w-3 h-3 shrink-0" style={{ color: accentText }} />
                        )}
                        <div className="min-w-0">
                          <p
                            className="font-semibold text-xs truncate"
                            style={{ color: textCol }}
                          >
                            {row.city}
                          </p>
                          {(row.skatepark || row.region) && (
                            <p
                              className="text-[9px] truncate"
                              style={{ color: subtleText }}
                            >
                              {row.skatepark}
                              {row.skatepark && row.region && " · "}
                              {row.region}
                            </p>
                          )}
                        </div>
                      </div>
                      <p
                        className="font-display text-base leading-none tabular-nums shrink-0"
                        style={{ color: isDark ? "#94d094" : accent }}
                      >
                        {row.votes}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showShare && (
          <div className="mt-4 flex items-center justify-between gap-2">
            <p
              className="text-[10px] font-medium uppercase tracking-[0.25em]"
              style={{ color: subtleText }}
            >
              Udostępnij
            </p>
            <div className="flex items-center gap-1">
              {[
                { id: "fb", Icon: Facebook },
                { id: "tw", Icon: Twitter },
                { id: "wa", Icon: MessageCircle },
                { id: "copy", Icon: Copy },
              ].map(({ id, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleShare(id)}
                  className="w-7 h-7 inline-flex items-center justify-center rounded-md transition-colors"
                  style={{
                    border: `1px solid ${borderCol}`,
                    color: subtleText,
                  }}
                  aria-label={id}
                >
                  <Icon className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        )}

        {showFooter && campaign && (
          <a
            href={window.location.origin}
            target="_top"
            rel="noopener noreferrer"
            className="block text-center mt-4 text-[10px] font-medium uppercase tracking-[0.25em] hover:opacity-80"
            style={{ color: subtleText }}
          >
            Zobacz pełne wyniki →
          </a>
        )}
      </div>

      {confirmation && (
        <VoteConfirmation
          option={confirmation.option}
          votes={confirmation.votes}
          onDone={() => setConfirmation(null)}
        />
      )}
    </div>
  );
}
